import {
  uniqueTradeDatesFromLatestByMarket,
  resolveMarketForStock,
  getTradeDateForMarket,
  serializeLatestByMarket,
  inferMarketFromSymbolId
} from './marketTradeDates';
import { authFetch } from './authFetch';

const SCREENER_STOCK_CACHE_LS_KEY = 'cachedStockData';
const STOCKS_BY_DATE_MEMORY_TTL = 60 * 60 * 1000; // 1 hour — align with PW index cache

/** @type {Map<string, { data: object[], timestamp: number }>} */
const memoryByDate = new Map();
/** @type {Map<string, Promise<object[]>>} */
const inFlightByDate = new Map();

/** Normalize trade_date to YYYY-MM-DD for comparison. */
const normalizeTradeDateYmd = (value) => {
  if (value == null || value === '') return '';
  return String(value).trim().slice(0, 10);
};

const normalizeFetchOptions = (secondArg) => {
  if (!secondArg) return {};
  if (typeof secondArg === 'object' && secondArg.date && Array.isArray(secondArg.data)) {
    return { screenerCache: secondArg };
  }
  return secondArg;
};

/**
 * Whether a screener row is the latest snapshot for its market.
 * @param {object} stock
 * @param {Record<string, string|null>} latestByMarket
 */
export const isStockAtMarketLatest = (stock, latestByMarket) => {
  if (!stock || !latestByMarket) return false;
  const market = resolveMarketForStock(stock);
  const expectedDate = getTradeDateForMarket(latestByMarket, market);
  if (!expectedDate) return false;
  return normalizeTradeDateYmd(stock.trade_date) === expectedDate;
};

/**
 * Build symbol_id → stock map keeping only rows at each market's latest trade date.
 * @param {object[][]} stockLists - One array of stocks per fetched trade date
 * @param {Record<string, string|null>} latestByMarket
 * @returns {Map<string, object>}
 */
export const indexStocksForLatestMarkets = (stockLists, latestByMarket) => {
  const stocksBySymbolId = new Map();
  if (!latestByMarket) return stocksBySymbolId;

  for (const stocks of stockLists) {
    if (!Array.isArray(stocks)) continue;
    for (const stock of stocks) {
      const id = stock && stock.symbol_id;
      if (!id || !isStockAtMarketLatest(stock, latestByMarket)) continue;
      stocksBySymbolId.set(String(id), stock);
    }
  }

  return stocksBySymbolId;
};

/** Store rows in shared memory cache (screener + PW readers). */
export const seedStocksCacheForDate = (date, data) => {
  const ymd = normalizeTradeDateYmd(date);
  if (!ymd || !Array.isArray(data) || data.length === 0) return;
  memoryByDate.set(ymd, { data, timestamp: Date.now() });
};

const readMemoryCacheForDate = (date) => {
  const ymd = normalizeTradeDateYmd(date);
  if (!ymd) return null;
  const entry = memoryByDate.get(ymd);
  if (!entry) return null;
  if (Date.now() - entry.timestamp >= STOCKS_BY_DATE_MEMORY_TTL) {
    memoryByDate.delete(ymd);
    return null;
  }
  return entry.data;
};

/** Legacy screener localStorage shape: { date, data, timestamp }. */
const readScreenerLocalStorageForDate = (date) => {
  const ymd = normalizeTradeDateYmd(date);
  if (!ymd) return null;
  try {
    const raw = localStorage.getItem(SCREENER_STOCK_CACHE_LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.date !== ymd || !Array.isArray(parsed.data) || parsed.data.length === 0) {
      return null;
    }
    const age = Date.now() - (parsed.timestamp || 0);
    if (age >= STOCKS_BY_DATE_MEMORY_TTL) return null;
    console.log(`[STOCKS BY MARKET] Using screener localStorage for ${ymd}`);
    seedStocksCacheForDate(ymd, parsed.data);
    return parsed.data;
  } catch {
    return null;
  }
};

export const clearStocksByDateMemoryCache = () => {
  memoryByDate.clear();
  inFlightByDate.clear();
};

/**
 * Fetch (or reuse) full stock list for one trade date.
 * Coalesces concurrent screener + portfolio/watchlist requests for the same date.
 * @param {string} date - YYYY-MM-DD
 * @param {{ screenerCache?: { date: string, data: object[] }|null, forceRefresh?: boolean }} [options]
 * @returns {Promise<object[]>}
 */
export const fetchStocksByTradeDate = async (date, options = {}) => {
  const ymd = normalizeTradeDateYmd(date);
  if (!ymd) return [];

  const { screenerCache = null, forceRefresh = false } = options;

  if (!forceRefresh) {
    const fromMemory = readMemoryCacheForDate(ymd);
    if (fromMemory) {
      console.log(`[STOCKS BY MARKET] Memory cache hit for ${ymd}`);
      return fromMemory;
    }

    if (screenerCache?.date === ymd && Array.isArray(screenerCache.data) && screenerCache.data.length > 0) {
      console.log(`[STOCKS BY MARKET] Using screener session cache for ${ymd}`);
      seedStocksCacheForDate(ymd, screenerCache.data);
      return screenerCache.data;
    }

    const fromLs = readScreenerLocalStorageForDate(ymd);
    if (fromLs) return fromLs;

    const inFlight = inFlightByDate.get(ymd);
    if (inFlight) {
      console.log(`[STOCKS BY MARKET] Coalescing in-flight fetch for ${ymd}`);
      return inFlight;
    }
  }

  const fetchPromise = (async () => {
    const response = await authFetch(`/api/stocks?date=${ymd}`);
    if (!response.ok) {
      console.warn(`[STOCKS BY MARKET] Failed to fetch stocks for ${ymd}: ${response.statusText}`);
      return [];
    }
    const stocks = await response.json();
    const rows = Array.isArray(stocks) ? stocks : [];
    if (rows.length > 0) {
      seedStocksCacheForDate(ymd, rows);
    }
    return rows;
  })();

  inFlightByDate.set(ymd, fetchPromise);
  try {
    return await fetchPromise;
  } finally {
    if (inFlightByDate.get(ymd) === fetchPromise) {
      inFlightByDate.delete(ymd);
    }
  }
};

/**
 * Build symbol_id → stock map from cached screener data and/or per-date API fetches.
 * Only indexes rows whose trade_date matches the latest date for that stock's market.
 * @param {Record<string, string>} latestByMarket - { US, CA }
 * @param {{ screenerCache?: object|null, forceRefresh?: boolean }|{ date?: string, data?: object[] }|null} fetchOptions
 * @returns {Promise<{ stocksBySymbolId: Map<string, object>, tradeDateKey: string, datesUsed: string[], latestByMarket: Record<string, string|null> }>}
 */
export const fetchStocksIndexForLatestMarkets = async (latestByMarket, fetchOptions = null) => {
  const options = normalizeFetchOptions(fetchOptions);
  const dates = uniqueTradeDatesFromLatestByMarket(latestByMarket);
  const results = await Promise.all(
    dates.map(async (date) => ({
      date,
      stocks: await fetchStocksByTradeDate(date, options),
    }))
  );

  const datesUsed = results
    .filter(({ stocks }) => stocks.length > 0)
    .map(({ date }) => date);

  const stockLists = results.map(({ stocks }) => stocks);
  const stocksBySymbolId = indexStocksForLatestMarkets(stockLists, latestByMarket);
  const tradeDateKey = serializeLatestByMarket(latestByMarket);

  return { stocksBySymbolId, tradeDateKey, datesUsed, latestByMarket };
};

/**
 * Resolve a stock row for symbol_id at the market-appropriate latest trade date.
 * @param {string} symbolId
 * @param {Map<string, object>} stocksBySymbolId
 * @param {Record<string, string|null>} latestByMarket
 * @param {object|null} symbolMarketHint - optional { market }
 * @returns {object|null}
 */
export const resolveStockForSymbol = (symbolId, stocksBySymbolId, latestByMarket, symbolMarketHint = null) => {
  if (!symbolId || !stocksBySymbolId || !latestByMarket) return null;

  const id = String(symbolId);
  const direct = stocksBySymbolId.get(id);
  if (direct && isStockAtMarketLatest(direct, latestByMarket)) return direct;

  const hintMarket = symbolMarketHint?.market || inferMarketFromSymbolId(id) || null;
  if (hintMarket) {
    const expectedDate = getTradeDateForMarket(latestByMarket, hintMarket);
    if (expectedDate) {
      for (const stock of stocksBySymbolId.values()) {
        if (String(stock.symbol_id) === id && normalizeTradeDateYmd(stock.trade_date) === expectedDate) {
          return stock;
        }
      }
    }
  }

  for (const stock of stocksBySymbolId.values()) {
    if (String(stock.symbol_id) === id && isStockAtMarketLatest(stock, latestByMarket)) {
      return stock;
    }
  }

  const expectedUs = latestByMarket.US;
  const expectedCa = latestByMarket.CA;
  console.warn(
    `[STOCKS BY MARKET] No latest row for ${id} (US=${expectedUs || 'n/a'}, CA=${expectedCa || 'n/a'})`
  );
  return null;
};

/**
 * @deprecated Use resolveStockForSymbol
 */
export const findStockForSymbol = (symbolId, stocksBySymbolId, latestByMarket, symbolMarketHint = null) =>
  resolveStockForSymbol(symbolId, stocksBySymbolId, latestByMarket, symbolMarketHint);

export { resolveMarketForStock, getTradeDateForMarket };
